/**
 * OfferSettings — employer/admin-only page to configure:
 *   - Statutory deductions (PF, ESI, PT, Income Tax, Insurance, Gratuity)
 *   - Default scenario presets (Desired / Balanced / Aggressive / Low + custom)
 *   - Fixed pay breakup (Basic / HRA / SA / LTA / Telephone)
 *   - Variable pay breakup (Monthly Perf / Annual Perf)
 *
 * Backed by GET/PUT/POST-reset /api/offer-settings.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, RotateCcw, Plus, Trash2, ArrowLeft } from "lucide-react";

const Section = ({ title, hint, children }) => (
    <div className="border border-border bg-white p-5 sm:p-6 space-y-3">
        <div>
            <h3 className="font-display font-bold text-lg">{title}</h3>
            {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
        </div>
        {children}
    </div>
);

const RowInput = ({ value, onChange, placeholder, type = "text", testid, w = "w-full" }) => (
    <Input value={value ?? ""} onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} placeholder={placeholder} type={type} className={`rounded-none h-9 ${w}`} data-testid={testid} />
);

export default function OfferSettings() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/offer-settings");
            setSettings(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const setField = (path, value) => {
        setSettings((s) => {
            const next = { ...s };
            const keys = path.split(".");
            let ref = next;
            for (let i = 0; i < keys.length - 1; i++) { ref[keys[i]] = Array.isArray(ref[keys[i]]) ? [...ref[keys[i]]] : { ...(ref[keys[i]] || {}) }; ref = ref[keys[i]]; }
            ref[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const updateRow = (listKey, idx, patch) => {
        setSettings((s) => {
            const list = [...(s[listKey] || [])];
            list[idx] = { ...list[idx], ...patch };
            return { ...s, [listKey]: list };
        });
    };
    const addRow = (listKey, template) => setSettings((s) => ({ ...s, [listKey]: [...(s[listKey] || []), template] }));
    const removeRow = (listKey, idx) => setSettings((s) => ({ ...s, [listKey]: (s[listKey] || []).filter((_, i) => i !== idx) }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                statutory_deductions: settings.statutory_deductions,
                scenario_presets: settings.scenario_presets,
                fixed_breakup: settings.fixed_breakup,
                variable_breakup: settings.variable_breakup,
                benefits_catalog: settings.benefits_catalog,
            };
            const { data } = await api.put("/offer-settings", payload);
            setSettings(data);
            toast.success("Offer settings saved");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const reset = async () => {
        if (!window.confirm("Reset offer settings to platform defaults?")) return;
        setSaving(true);
        try {
            const { data } = await api.post("/offer-settings/reset");
            setSettings(data);
            toast.success("Reset to defaults");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const fixedTotal = useMemo(() => (settings?.fixed_breakup || []).reduce((s, x) => s + (Number(x.pct_of_fixed) || 0), 0), [settings]);
    const variableTotal = useMemo(() => (settings?.variable_breakup || []).reduce((s, x) => s + (Number(x.pct_of_variable) || 0), 0), [settings]);

    if (loading || !settings) return <div className="p-10 text-center text-sm text-muted-foreground">Loading offer settings…</div>;

    return (
        <div className="max-w-[1200px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-5" data-testid="offer-settings">
            <div className="sticky top-0 z-20 -mx-3 sm:-mx-4 px-3 sm:px-4 py-3 bg-background/95 backdrop-blur border-b border-border flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <Link to="/" className="inline-flex items-center gap-1 text-xs font-mono text-muted-foreground hover:text-ikb" data-testid="back-home"><ArrowLeft className="w-3.5 h-3.5" /> Back</Link>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-mono text-ikb">Company · Compensation</div>
                        <h1 className="font-display font-black text-xl sm:text-2xl">Offer structure setup</h1>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={reset} disabled={saving} className="rounded-none h-9" data-testid="offer-settings-reset"><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset</Button>
                    <Button onClick={save} disabled={saving} className="rounded-none h-9 bg-ikb hover:bg-[#001f7a] text-white" data-testid="offer-settings-save"><Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}</Button>
                </div>
            </div>

            {/* Scenario presets */}
            <Section title="Default scenario presets" hint="Each preset defines an increment % and how that new CTC is split between Fixed and Variable pools.">
                <div className="space-y-2">
                    {(settings.scenario_presets || []).map((p, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center border border-border p-2" data-testid={`preset-${i}`}>
                            <RowInput value={p.key} onChange={(v) => updateRow("scenario_presets", i, { key: v })} placeholder="key" w="col-span-2" testid={`preset-${i}-key`} />
                            <RowInput value={p.label} onChange={(v) => updateRow("scenario_presets", i, { label: v })} placeholder="label" w="col-span-3" testid={`preset-${i}-label`} />
                            <div className="col-span-2"><Label className="text-[9px] font-mono uppercase">Increment %</Label><RowInput type="number" value={p.increment_pct} onChange={(v) => updateRow("scenario_presets", i, { increment_pct: v })} testid={`preset-${i}-inc`} /></div>
                            <div className="col-span-2"><Label className="text-[9px] font-mono uppercase">Fixed % of CTC</Label><RowInput type="number" value={p.fixed_pct_of_ctc} onChange={(v) => updateRow("scenario_presets", i, { fixed_pct_of_ctc: v })} testid={`preset-${i}-fixed`} /></div>
                            <div className="col-span-2"><Label className="text-[9px] font-mono uppercase">Var monthly %</Label><RowInput type="number" value={p.variable_split_monthly_pct} onChange={(v) => updateRow("scenario_presets", i, { variable_split_monthly_pct: v })} testid={`preset-${i}-var`} /></div>
                            <button onClick={() => removeRow("scenario_presets", i)} className="col-span-1 text-rose-600 hover:bg-rose-50 p-1" data-testid={`preset-${i}-del`}><Trash2 className="w-3.5 h-3.5" /></button>
                            <div className="col-span-12 -mt-1"><RowInput value={p.description} onChange={(v) => updateRow("scenario_presets", i, { description: v })} placeholder="Short description" testid={`preset-${i}-desc`} /></div>
                        </div>
                    ))}
                </div>
                <Button variant="outline" onClick={() => addRow("scenario_presets", { key: "custom_" + Date.now(), label: "New preset", increment_pct: 20, fixed_pct_of_ctc: 75, variable_split_monthly_pct: 60, color: "blue", description: "" })} className="rounded-none h-9" data-testid="preset-add"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add preset</Button>
            </Section>

            {/* Statutory deductions */}
            <Section title="Statutory & mandatory deductions" hint="Will be auto-injected into every new offer scenario when marked as mandatory.">
                <div className="space-y-2">
                    {(settings.statutory_deductions || []).map((d, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center border border-border p-2" data-testid={`ded-${i}`}>
                            <RowInput value={d.key} onChange={(v) => updateRow("statutory_deductions", i, { key: v })} w="col-span-2" placeholder="key" testid={`ded-${i}-key`} />
                            <RowInput value={d.label} onChange={(v) => updateRow("statutory_deductions", i, { label: v })} w="col-span-3" placeholder="label" testid={`ded-${i}-label`} />
                            <select value={d.calc_type} onChange={(e) => updateRow("statutory_deductions", i, { calc_type: e.target.value })} className="col-span-2 h-9 border border-border bg-white text-xs" data-testid={`ded-${i}-calc`}>
                                <option value="percentage_of_basic">% of Basic</option>
                                <option value="percentage_of_gross">% of Gross</option>
                                <option value="flat_monthly">Flat / month</option>
                                <option value="flat_annual">Flat / year</option>
                            </select>
                            <RowInput type="number" value={d.value} onChange={(v) => updateRow("statutory_deductions", i, { value: v })} placeholder="Value" w="col-span-1" testid={`ded-${i}-val`} />
                            <RowInput type="number" value={d.cap_monthly ?? ""} onChange={(v) => updateRow("statutory_deductions", i, { cap_monthly: v === "" ? null : v })} placeholder="Cap/mo" w="col-span-1" testid={`ded-${i}-cap`} />
                            <select value={d.borne_by} onChange={(e) => updateRow("statutory_deductions", i, { borne_by: e.target.value })} className="col-span-1 h-9 border border-border bg-white text-xs" data-testid={`ded-${i}-borne`}>
                                <option value="employee">Empl.</option>
                                <option value="employer">Emper.</option>
                                <option value="both">Both</option>
                            </select>
                            <label className="col-span-1 flex items-center gap-1 text-[10px]" data-testid={`ded-${i}-mandatory-row`}><input type="checkbox" checked={!!d.mandatory} onChange={(e) => updateRow("statutory_deductions", i, { mandatory: e.target.checked })} className="accent-ikb" data-testid={`ded-${i}-mandatory`} />Mand.</label>
                            <button onClick={() => removeRow("statutory_deductions", i)} className="col-span-1 text-rose-600 hover:bg-rose-50 p-1 justify-self-end" data-testid={`ded-${i}-del`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                    ))}
                </div>
                <Button variant="outline" onClick={() => addRow("statutory_deductions", { key: "custom_" + Date.now(), label: "New deduction", calc_type: "flat_monthly", value: 0, cap_monthly: null, borne_by: "employee", mandatory: true, order: 99 })} className="rounded-none h-9" data-testid="ded-add"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add deduction</Button>
            </Section>

            {/* Fixed breakup */}
            <Section title="Fixed pay breakup" hint={`How the Fixed pool is split. Total should be 100%. Currently: ${fixedTotal}%.`}>
                <div className="space-y-2">
                    {(settings.fixed_breakup || []).map((it, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center border border-border p-2" data-testid={`fix-${i}`}>
                            <RowInput value={it.key} onChange={(v) => updateRow("fixed_breakup", i, { key: v })} w="col-span-3" placeholder="key" testid={`fix-${i}-key`} />
                            <RowInput value={it.label} onChange={(v) => updateRow("fixed_breakup", i, { label: v })} w="col-span-6" placeholder="label" testid={`fix-${i}-label`} />
                            <RowInput type="number" value={it.pct_of_fixed} onChange={(v) => updateRow("fixed_breakup", i, { pct_of_fixed: v })} placeholder="% of fixed" w="col-span-2" testid={`fix-${i}-pct`} />
                            <button onClick={() => removeRow("fixed_breakup", i)} className="col-span-1 text-rose-600 hover:bg-rose-50 p-1 justify-self-end" data-testid={`fix-${i}-del`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                    ))}
                </div>
                <Button variant="outline" onClick={() => addRow("fixed_breakup", { key: "custom_" + Date.now(), label: "New head", pct_of_fixed: 0, order: 99 })} className="rounded-none h-9" data-testid="fix-add"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add head</Button>
            </Section>

            {/* Variable breakup */}
            <Section title="Variable pay breakup" hint={`The first row is paid MONTHLY (uses variable_split_monthly_pct from the preset); remaining rows split the annual pool. Total: ${variableTotal}%.`}>
                <div className="space-y-2">
                    {(settings.variable_breakup || []).map((it, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center border border-border p-2" data-testid={`var-${i}`}>
                            <RowInput value={it.key} onChange={(v) => updateRow("variable_breakup", i, { key: v })} w="col-span-3" placeholder="key" testid={`var-${i}-key`} />
                            <RowInput value={it.label} onChange={(v) => updateRow("variable_breakup", i, { label: v })} w="col-span-6" placeholder="label" testid={`var-${i}-label`} />
                            <RowInput type="number" value={it.pct_of_variable} onChange={(v) => updateRow("variable_breakup", i, { pct_of_variable: v })} placeholder="% of variable" w="col-span-2" testid={`var-${i}-pct`} />
                            <button onClick={() => removeRow("variable_breakup", i)} className="col-span-1 text-rose-600 hover:bg-rose-50 p-1 justify-self-end" data-testid={`var-${i}-del`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                    ))}
                </div>
                <Button variant="outline" onClick={() => addRow("variable_breakup", { key: "custom_" + Date.now(), label: "New bonus", pct_of_variable: 0, order: 99 })} className="rounded-none h-9" data-testid="var-add"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add row</Button>
            </Section>

            <div className="flex justify-end pt-2 pb-12">
                <Button onClick={save} disabled={saving} className="rounded-none h-10 bg-ikb hover:bg-[#001f7a] text-white" data-testid="offer-settings-save-bottom"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}</Button>
            </div>
        </div>
    );
}
